import React from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Grid,
    Divider,
    useTheme,
    LinearProgress,
    Tooltip,
    Chip,
} from '@mui/material';
import { 
    SignalCellularAlt as SignalIcon, 
    Speed as SpeedIcon,
    Memory as MemoryIcon,
    Lock as LockIcon
} from '@mui/icons-material';
import { Device } from '../../types/Device';
import SparklineChart from '../common/SparklineChart';
import { useDeviceRealtime } from '../../hooks/useDeviceRealtime';
import { format } from 'date-fns';

interface ModoStatusProps {
    device: Device;
}

const ModoStatus: React.FC<ModoStatusProps> = ({ device }) => {
    const theme = useTheme();
    
    // Get real-time data from MQTT
    const { realtimeData } = useDeviceRealtime(device.id);
    
    // Use real-time data when available, fallback to latest_status
    const displayGnssSamples = realtimeData.gnss_samples !== null 
        ? realtimeData.gnss_samples 
        : (device.latest_status?.gnss_samples ?? []);
    
    const displayOcxoControl = realtimeData.ocxo_control !== null 
        ? realtimeData.ocxo_control 
        : (device.latest_status?.ocxo_control?.at(-1) ?? 'N/A');
    
    // Note: displayLockStatus, displayNGnss, displayTemperature, and getLockStatusColor 
    // were removed as they're no longer needed after ADS-B DTOE card simplification

    // Helper to safely get arrays with proper type checking
    const getSafeGnssSamples = (): number[] => {
        const samples = device.latest_status?.gnss_samples;
        return Array.isArray(samples) && samples.length > 1 ? samples.slice(-50) : [];
    };

    const getSafeOcxoControl = (): number[] => {
        const control = device.latest_status?.ocxo_control;
        return Array.isArray(control) && control.length > 1 ? control.slice(-50) : [];
    };

    // Check if we should show the sparkline charts
    const shouldShowGnssChart = (): boolean => {
        const samples = device.latest_status?.gnss_samples;
        return Boolean(samples && Array.isArray(samples) && samples.length > 1);
    };

    const shouldShowOcxoChart = (): boolean => {
        const control = device.latest_status?.ocxo_control;
        return Boolean(control && Array.isArray(control) && control.length > 1);
    };
    
    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <SpeedIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
                    <Typography variant="h6">
                        Real-time Performance
                    </Typography>
                </Box>
                {realtimeData.lastUpdated && (
                    <Chip
                        label={`Last updated: ${format(new Date(realtimeData.lastUpdated), 'HH:mm:ss')}`}
                        size="small"
                        color="primary"
                        variant="outlined"
                    />
                )}
            </Box>

            <Grid container spacing={3}>
                {/* Main Metrics */}
                <Grid item xs={12} lg={8}>
                    <Grid container spacing={3}>
                        {/* GNSS Samples with Multiple Values */}
                        <Grid item xs={12} md={6}>
                            <Card 
                                variant="outlined" 
                                sx={{ 
                                    height: '100%',
                                    border: realtimeData.gnss_samples !== null ? '2px solid #4caf50' : undefined,
                                    transition: 'border-color 0.3s ease'
                                }}
                            >
                                <CardContent>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <SignalIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
                                            <Typography variant="subtitle1" fontWeight={500}>
                                                GNSS Samples
                                            </Typography>
                                        </Box>
                                        {realtimeData.gnss_samples !== null && (
                                            <Chip
                                                label="LIVE"
                                                size="small"
                                                color="success"
                                                sx={{ fontSize: '0.7rem', height: '20px' }}
                                            />
                                        )}
                                    </Box>
                                    
                                    {displayGnssSamples.length > 0 ? (
                                        <Box>
                                            {displayGnssSamples.map((sample, index) => (
                                                <Box key={index} sx={{ mb: 1.5 }}>
                                                    <Typography variant="body2" color="text.secondary">
                                                        GNSS Sample {index + 1}:
                                                    </Typography>
                                                    <Typography variant="h5" color="primary" fontWeight={600}>
                                                        {sample.toFixed(2)} ps
                                                    </Typography>
                                                </Box>
                                            ))}
                                        </Box>
                                    ) : (
                                        <Typography variant="h4" color="text.secondary">
                                            N/A
                                        </Typography>
                                    )}
                                    
                                    {shouldShowGnssChart() && (
                                        <Box sx={{ mt: 2, height: 60 }}>
                                            <SparklineChart 
                                                data={getSafeGnssSamples()} 
                                                color={theme.palette.primary.main}
                                                height={60}
                                            />
                                        </Box>
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>
                        
                        {/* OCXO Control with Sparkline */}
                        <Grid item xs={12} md={6}>
                            <Card 
                                variant="outlined" 
                                sx={{ 
                                    height: '100%',
                                    border: realtimeData.ocxo_control !== null ? '2px solid #4caf50' : undefined,
                                    transition: 'border-color 0.3s ease'
                                }}
                            >
                                <CardContent>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <MemoryIcon sx={{ mr: 1, color: theme.palette.secondary.main }} />
                                            <Typography variant="subtitle1" fontWeight={500}>
                                                OCXO Control
                                            </Typography>
                                        </Box>
                                        {realtimeData.ocxo_control !== null && (
                                            <Chip
                                                label="LIVE"
                                                size="small"
                                                color="success"
                                                sx={{ fontSize: '0.7rem', height: '20px' }}
                                            />
                                        )}
                                    </Box>
                                    
                                    <Typography variant="h4" color="secondary" gutterBottom>
                                        {typeof displayOcxoControl === 'number' ? displayOcxoControl.toFixed(2) : displayOcxoControl}
                                    </Typography>
                                    
                                    {shouldShowOcxoChart() && (
                                        <Box sx={{ mt: 2, height: 60 }}>
                                            <SparklineChart 
                                                data={getSafeOcxoControl()} 
                                                color={theme.palette.secondary.main}
                                                height={60}
                                            />
                                        </Box>
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                </Grid>
                
                {/* ADS-B DTOE */}
                <Grid item xs={12} lg={4}>
                    <Card 
                        variant="outlined" 
                        sx={{ 
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            border: typeof realtimeData.adsb_dtoe === 'number' ? '2px solid #4caf50' : undefined,
                            transition: 'border-color 0.3s ease'
                        }}
                    >
                        <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <MemoryIcon sx={{ mr: 1, color: theme.palette.info.main }} />
                                    <Typography variant="subtitle1" fontWeight={500}>
                                        ADS-B DTOE
                                    </Typography>
                                </Box>
                                {typeof realtimeData.adsb_dtoe === 'number' && (
                                    <Chip
                                        label="LIVE"
                                        size="small"
                                        color="success"
                                        sx={{ fontSize: '0.7rem', height: '20px' }}
                                    />
                                )}
                            </Box>
                            
                            {/* Value - left aligned under title */}
                            <Box>
                                {typeof realtimeData.adsb_dtoe === 'number' ? (
                                    <Typography variant="h4" color="info.main" gutterBottom>
                                        {realtimeData.adsb_dtoe.toFixed(2)}
                                    </Typography>
                                ) : (
                                    <Typography variant="h4" color="text.secondary" gutterBottom>
                                        N/A
                                    </Typography>
                                )}
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
};

export default ModoStatus; 